import React from 'react';
import * as lib from '~/dependencies';
import { eventTracker } from '~/helpers';
import { Message, SettingsLink } from './styles';

/**
 * A fixed id, so react-toastify de-duplicates for free and `dismiss` can target this toast
 * alone. Two concurrent calls share one microphone, so they must never stack two warnings.
 */
export const MIC_WARNING_TOAST_ID = 'mic-audio-warning';

/** Set while the app closes the toast itself, so `onClose` can tell that apart from a user click. */
let dismissedProgrammatically = false;

interface MicAudioWarningProps {
  onOpenSettings: () => void;
}

/**
 * @name MicAudioWarning
 * @description Body of the toast raised when the local microphone is producing no audio during a
 * call. The "Voice Settings" link opens the dialog of the same name, which carries the microphone
 * picker and a live level meter - the two things the user needs to fix this without hanging up.
 *
 * The link text is the dialog's own translated title in every locale, so it names the window it
 * opens rather than describing it.
 */
export default function MicAudioWarning({
  onOpenSettings,
}: MicAudioWarningProps) {
  function onLinkClick() {
    eventTracker(events => events.MicAudioWarningClicked);
    onOpenSettings();
  }

  return (
    <Message data-test-id="mic-audio-warning">
      <lib.Trans
        i18nKey="components.micAudioWarning.message"
        components={{
          a: (
            <SettingsLink
              type="button"
              data-testid="mic-audio-warning-link"
              onClick={onLinkClick}
            />
          ),
        }}
      />
    </Message>
  );
}

/**
 * @name showMicAudioWarning
 * @description Raise the microphone warning as a persistent top-center toast. Wrapped here so the
 * plain-TypeScript monitor never has to build JSX.
 *
 * `autoClose: false` keeps it up until the microphone recovers or the user closes it. It is set
 * through `options` rather than the `time` argument because `ToastMessage` spreads `options` last
 * and its `time` parameter is a number. `closeOnClick` is off because the body contains a link
 * and a stray click must not eat it.
 * @param {() => void} onOpenSettings - opens the Voice Settings dialog
 * @param {() => void} onDismiss - the user closed it; the caller stays quiet for the call
 */
export function showMicAudioWarning(
  onOpenSettings: () => void,
  onDismiss: () => void
): void {
  dismissedProgrammatically = false;

  lib.ToastMessage(
    'warn',
    <MicAudioWarning onOpenSettings={onOpenSettings} />,
    undefined,
    {
      autoClose: false,
      toastId: MIC_WARNING_TOAST_ID,
      closeOnClick: false,
      draggable: false,
      closeButton: true,
      // react-toastify 5 calls `onClose` for every close and tells us nothing about who did it,
      // so the automatic path flags itself first. Without this the auto-dismiss below would be
      // recorded as "the user dismissed it" and silence the warning for the rest of the call.
      onClose: () => {
        if (!dismissedProgrammatically) {
          onDismiss();
        }
      },
    }
  );
}

/**
 * @name dismissMicAudioWarning
 * @description Take the warning down on the app's own initiative - the microphone started
 * producing audio again, or the call ended. Deliberately does NOT count as a user dismissal.
 */
export function dismissMicAudioWarning(): void {
  dismissedProgrammatically = true;
  lib.toast.dismiss(MIC_WARNING_TOAST_ID);
}
